import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config/config';

const NewVoiceAssistant = ({ compact = false, isOpen = false, onClose = () => {}, initialMessage = "" }) => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en-US');
  const [conversation, setConversation] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [manualInput, setManualInput] = useState('');

  const recognitionRef = useRef(null);
  const initializedRef = useRef(false);

  // Available languages
  const languages = [
    { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
    { code: 'te-IN', name: 'Telugu', flag: '🇮🇳' },
    { code: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
    { code: 'ta-IN', name: 'Tamil', flag: '🇮🇳' },
    { code: 'kn-IN', name: 'Kannada', flag: '🇮🇳' },
    { code: 'ml-IN', name: 'Malayalam', flag: '🇮🇳' },
    { code: 'mr-IN', name: 'Marathi', flag: '🇮🇳' },
    { code: 'bn-IN', name: 'Bengali', flag: '🇮🇳' },
    { code: 'es-ES', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
    { code: 'de-DE', name: 'German', flag: '🇩🇪' },
    { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' }
  ];

  // Get intelligent fallback responses
  const getIntelligentResponse = (message, language) => {
    const lowerMessage = message.toLowerCase();
    
    // Programming responses
    if (lowerMessage.includes('python') || lowerMessage.includes('పైథాన్') || lowerMessage.includes('पायथन')) {
    return language.includes('te') ? 
    'పైథాన్ ఒక శక్తివంతమైన ప్రోగ్రామింగ్ భాష. ఇది వెబ్ డెవలప్‌మెంట్, డేటా సైన్స్, AI, మరియు ఆటోమేషన్ కోసం విస్తృతంగా ఉపయోగించబడుతుంది.' :
    language.includes('hi') ? 
    'पायथन एक शक्तिशाली प्रोग्रामिंग भाषा है। यह वेब डेवलपमेंट, डेटा साइंस, AI, और ऑटोमेशन के लिए व्यापक रूप से उपयोग की जाती है।' :
    'Python is a powerful programming language. It\'s widely used for web development, data science, AI, and automation.';
    }
    
    if (lowerMessage.includes('javascript') || lowerMessage.includes('js') || lowerMessage.includes('జావాస్క్రిప్ట్')) {
    return language.includes('te') ? 
    'జావాస్క్రిప్ట్ వెబ్ డెవలప్‌మెంట్‌లో ఉపయోగించే ప్రధాన ప్రోగ్రామింగ్ భాష.' :
    language.includes('hi') ? 
    'जावास्क्रिप्ट वेब डेवलपमेंट में उपयोग की जाने वाली मुख्य प्रोग्रामिंग भाषा है।' :
    'JavaScript is the main programming language used in web development.';
    }
    
    // Greetings
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('హలో') || lowerMessage.includes('नमस्ते')) {
    return language.includes('te') ? 
    'హలో! నేను మీకు సహాయం చేయడానికి ఇక్కడ ఉన్నాను. మీరు ఏదైనా ప్రశ్న అడగవచ్చు.' :
    language.includes('hi') ? 
    'हैलो! मैं आपकी मदद करने के लिए यहाँ हूँ। आप कोई भी सवाल पूछ सकते हैं।' :
    'Hello! I\'m here to help you. You can ask me any question.';
    }
    
    // Help
    if (lowerMessage.includes('help') || lowerMessage.includes('సహాయం') || lowerMessage.includes('मदद')) {
    return language.includes('te') ? 
    'నేను ప్రోగ్రామింగ్, సాంకేతిక విషయాలు, మరియు సాధారణ జ్ఞానం గురించి సహాయం చేయగలను.' :
    language.includes('hi') ? 
    'मैं प्रोग्रामिंग, तकनीकी विषयों, और सामान्य ज्ञान में मदद कर सकता हूं।' :
    'I can help you with programming, technical topics, and general knowledge.';
    }
    
    // Question words - provide more detailed responses
    if (lowerMessage.includes('what') || lowerMessage.includes('how') || lowerMessage.includes('why') || 
    lowerMessage.includes('ఏమి') || lowerMessage.includes('ఎలా') || lowerMessage.includes('ఎందుకు') ||
    lowerMessage.includes('क्या') || lowerMessage.includes('कैसे') || lowerMessage.includes('क्यों')) {
    return language.includes('te') ? 
    'ఇది ఒక మంచి ప్రశ్న! నేను మీకు సహాయం చేయడానికి ఇక్కడ ఉన్నాను. మీ ప్రశ్నకు సమాధానం ఇవ్వడానికి నేను ప్రయత్నిస్తాను.' :
    language.includes('hi') ? 
    'यह एक अच्छा सवाल है! मैं आपकी मदद करने के लिए यहाँ हूँ। मैं आपके सवाल का जवाब देने की कोशिश करूंगा।' :
    'That\'s a great question! I\'m here to help you. I\'ll try to answer your question.';
    }
    
    // Default response
    return language.includes('te') ? 
    'నేను మీ ప్రశ్నను విన్నాను! నేను మీకు సహాయం చేయడానికి ఇక్కడ ఉన్నాను.' :
    language.includes('hi') ? 
    'मैंने आपका सवाल सुना है! मैं आपकी मदद करने के लिए यहाँ हूँ।' :
    'I heard your question! I\'m here to help you.';
  };

  // Get error message in appropriate language
  const getErrorMessage = (language) => {
    return language.includes('te') ? 
    'క్షమించండి, నేను మీ ప్రశ్నకు సమాధానం ఇవ్వలేకపోయాను. దయచేసి మళ్లీ ప్రయత్నించండి.' :
    language.includes('hi') ? 
    'माफ़ करें, मैं आपके सवाल का जवाब नहीं दे पा रहा। कृपया दोबारा कोशिश करें।' :
    'Sorry, I couldn\'t process your question. Please try again.';
  };

  // Speak text using speech synthesis
  const speak = (text) => {
    // UI instruction: user should able to read not hear
    console.log('🔊 Speech output disabled per requirement. Text to read:', text);
    setIsSpeaking(false);
  };

  // Get AI response - calls backend API
  const getAIResponse = React.useCallback(async (message, language) => {
    console.log(`🤖 Getting AI response for: "${message}" in ${language}`);
    
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.userId || "guest";
      
      const langLabel = languages.find(l => l.code === language)?.name || language;
      
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("token")}`,
          'X-Session-Token': localStorage.getItem("sessionToken") || ""
        },
        body: JSON.stringify({
          message: message,
          langMode: "fixed",
          langCode: language,
          langLabel: langLabel,
          userId: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`🤖 Backend response: "${data.reply}"`);
        return data.reply;
      } else {
        console.error('❌ Backend API error:', response.status);
        return getIntelligentResponse(message, language);
      }
    } catch (error) {
      console.error('❌ Backend API error:', error);
      return getIntelligentResponse(message, language);
    }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentLanguage]);

  // Handle user message and get AI response
  const handleUserMessage = React.useCallback(async (message) => {
    if (!message) return;
    try {
      setIsSpeaking(true);
      
      // Get AI response
      const response = await getAIResponse(message, currentLanguage);
      
      // Add AI response to conversation
      setConversation(prev => [...prev, { type: 'assistant', message: response, timestamp: new Date() }]);
      
      // Speak the response
      speak(response);
      
    } catch (error) {
      console.error('❌ Error handling message:', error);
      const errorResponse = getErrorMessage(currentLanguage);
      setConversation(prev => [...prev, { type: 'assistant', message: errorResponse, timestamp: new Date() }]);
      speak(errorResponse);
    }
  }, [currentLanguage, getAIResponse]);

  // Process manual text input (e.g. from initialMessage)
  const processManualMessage = React.useCallback(async (text) => {
    if (!text) return;
    setConversation(prev => [...prev, { type: 'user', message: text, timestamp: new Date() }]);
    await handleUserMessage(text);
  }, [handleUserMessage]);

  // Handle initial message
  useEffect(() => {
    if (isOpen && initialMessage && !initializedRef.current) {
      processManualMessage(initialMessage);
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, initialMessage, processManualMessage]);

  // Handle direct message from parent (vaInitialMessage update)
  useEffect(() => {
    if (isOpen && initialMessage) {
      // Direct update to conversation to allow reading
      setConversation(prev => {
        // Only replace if the last message is different or it's a new conversation
        if (prev.length === 0 || prev[prev.length-1].message !== initialMessage) {
          return [{ type: 'assistant', message: initialMessage, timestamp: new Date() }];
        }
        return prev;
      });
    }
  }, [isOpen, initialMessage]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!recognitionRef.current) {
        recognitionRef.current = new SpeechRecognition();
      }
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = currentLanguage;

      recognitionRef.current.onstart = () => {
        console.log('🎤 Speech recognition started');
        setIsListening(true);
      };

      recognitionRef.current.onresult = async (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('🎤 Recognized:', transcript);
        
        // Add user message to conversation
        setConversation(prev => [...prev, { type: 'user', message: transcript, timestamp: new Date() }]);
        
        // Process the message
        await handleUserMessage(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('❌ Speech recognition error:', event.error);
        setIsListening(false);

        let errorMsg = '';
        let shouldRetry = false;

        switch(event.error) {
          case 'network':
            errorMsg = '🌐 Network error: Please check your internet connection and ensure you are using HTTPS.';
            shouldRetry = retryCount < 3;
            break;
          case 'timeout':
            errorMsg = '⏱️ Timeout: Please speak louder or closer to microphone';
            shouldRetry = retryCount < 2;
            break;
          case 'no-speech':
            errorMsg = '🔇 No speech detected: Please try speaking again';
            shouldRetry = retryCount < 2;
            break;
          case 'audio-capture':
            errorMsg = '🎤 Microphone error: Please check microphone permissions';
            shouldRetry = false;
            break;
          case 'not-allowed':
            errorMsg = '🔒 Microphone permission denied: Please allow microphone access';
            shouldRetry = false;
            break;
          default:
            errorMsg = `❌ Error: ${event.error}. Please try again.`;
            shouldRetry = retryCount < 2;
        }

        setError(errorMsg);

        if (shouldRetry) {
          setRetryCount(prev => prev + 1);
          console.log(`🔄 Retrying speech recognition (${retryCount + 1}/3)...`);
          setTimeout(() => {
            if (recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (err) {
                console.error('Retry failed:', err);
              }
            }
          }, 1000);
        } else {
          setRetryCount(0);
        }
      };

      recognitionRef.current.onend = () => {
        console.log('🎤 Speech recognition ended');
        setIsListening(false);
      };
    }
  }, [currentLanguage, handleUserMessage]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        setError(null);
        setRetryCount(0);
        recognitionRef.current.start();
      } catch (err) {
        console.error("Failed to start recognition:", err);
        setError('❌ Failed to start speech recognition. Please try again.');
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  // Change language
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode);
    if (recognitionRef.current) {
      recognitionRef.current.lang = langCode;
    }
    console.log(`🌍 Language changed to: ${langCode}`);
  };

  // Clear conversation
  const clearConversation = () => {
    setConversation([]);
  };

 if (!isOpen && !compact) return null;

  return (
    <div className={`${compact ? 'bg-transparent' : 'fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm'}`}>
      <div className={`${compact ? 'w-full' : 'w-full max-w-4xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 rounded-3xl shadow-2xl flex flex-col relative'}`}>
        {/* Close Button for Modal Mode */}
        {!compact && (
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className={`${compact ? '' : 'overflow-y-auto p-8'}`}>
        {/* Back Button for Full Screen Mode */}
        {!compact && (
          <button 
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors font-bold text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl"
          >
            ← Back to Learning
          </button>
        )}

        {/* Header */}
 {!compact && (
 <div className="text-center mb-8">
 <h1 className="text-4xl font-bold text-white mb-2">
 🤖 Smart Voice Assistant
 </h1>
 <p className="text-blue-200">
 Ask me anything! I can help with programming, technology, and general questions.
 </p>
 </div>
 )}

 {/* Language Selector */}
 <div className={`${compact ? 'p-3 mb-3' : 'p-6 mb-6'} bg-white/10 backdrop-blur-lg rounded-xl`}>
 <h3 className="text-white text-sm font-semibold mb-3">🌍 Select Language</h3>
        <div className="flex flex-wrap gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                currentLanguage === lang.code
                  ? 'bg-blue-600 text-white shadow-lg ring-2 ring-white/40'
                  : 'bg-white/20 text-white hover:bg-white/30 border border-white/10'
              }`}
            >
              <span className="mr-1">{lang.flag}</span> 
              {lang.name}
            </button>
          ))}
        </div>
 </div>

 {/* Voice Controls */}
 <div className={`${compact ? 'p-3 mb-3' : 'p-6 mb-6'} bg-white/10 backdrop-blur-lg rounded-xl`}>
 <div className="flex items-center justify-center gap-4">
 <button
 onClick={startListening}
 disabled={isListening || isSpeaking}
 className={`${compact ? 'w-12 h-12 text-xl' : 'w-16 h-16 text-2xl'} rounded-full flex items-center justify-center transition-all ${
 isListening
 ? 'bg-red-500 text-white animate-pulse'
 : 'bg-green-500 text-white hover:bg-green-600'
 } ${isSpeaking ? 'opacity-50 cursor-not-allowed' : ''}`}
 >
 🎤
 </button>
 
 <button
 onClick={stopListening}
 disabled={!isListening}
 className={`${compact ? 'w-10 h-10 text-lg' : 'w-12 h-12 text-xl'} rounded-full flex items-center justify-center transition-all ${
 isListening
 ? 'bg-red-500 text-white hover:bg-red-600'
 : 'bg-gray-500 text-white opacity-50 cursor-not-allowed'
 }`}
 >
 ⏹️
 </button>
 
 <button
 onClick={clearConversation}
 className={`${compact ? 'w-10 h-10 text-lg' : 'w-12 h-12 text-xl'} rounded-full bg-blue-500 text-white hover:bg-blue-600 flex items-center justify-center transition-all`}
 >
 🗑️
 </button>
 </div>
 
 <div className="text-center mt-3">
 {error && (
   <p className="text-red-300 text-sm font-semibold mb-2">
     {error}
   </p>
 )}
 {isListening && (
   <p className="text-green-300 text-sm font-semibold animate-pulse">
     🎤 Listening...
   </p>
 )}
 {isSpeaking && (
   <p className="text-blue-300 text-sm font-semibold animate-pulse">
     🤖 Processing response...
   </p>
 )}
          {!isListening && !isSpeaking && !error && (
            <p className="text-white/60 text-sm font-medium">
              Click microphone to talk
            </p>
          )}
 </div>
 </div>

 {/* Manual Text Input (Fallback) */}
 <div className={`${compact ? 'p-3 mb-3' : 'p-6 mb-6'} bg-white/10 backdrop-blur-lg rounded-xl`}>
 <h3 className="text-white text-sm font-semibold mb-3">📝 Or Type Your Question</h3>
 <div className="flex gap-2">
 <input
   type="text"
   value={manualInput}
   onChange={(e) => setManualInput(e.target.value)}
   onKeyPress={(e) => {
     if (e.key === 'Enter' && manualInput.trim()) {
       handleUserMessage(manualInput);
       setManualInput('');
     }
   }}
   placeholder="Type your question here..."
   className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-white/60 focus:bg-white/30"
 />
 <button
   onClick={() => {
     if (manualInput.trim()) {
       handleUserMessage(manualInput);
       setManualInput('');
     }
   }}
   disabled={isSpeaking || !manualInput.trim()}
   className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
 >
   Send
 </button>
 </div>
 </div>

 {/* Quick Test Buttons */}
 <div className={`${compact ? 'p-3 mb-3' : 'p-6 mb-6'} bg-white/10 backdrop-blur-lg rounded-xl`}>
 <h3 className="text-white text-sm font-semibold mb-3">🧪 Quick Tests</h3>
 <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'} gap-2`}>
 <button
 onClick={() => handleUserMessage('Hello, how are you?')}
 disabled={isSpeaking}
 className="p-2 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-all disabled:opacity-50"
 >
 👋 Say Hello
 </button>
 <button
 onClick={() => handleUserMessage('What is Python?')}
 disabled={isSpeaking}
 className="p-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg transition-all disabled:opacity-50"
 >
 🐍 Ask about Python
 </button>
 <button
 onClick={() => handleUserMessage('Can you help me?')}
 disabled={isSpeaking}
 className="p-2 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded-lg transition-all disabled:opacity-50"
 >
 ❓ Ask for Help
 </button>
 </div>
 </div>

 {/* Conversation History */}
 {conversation.length > 0 && (
 <div className={`${compact ? 'p-3' : 'p-6'} bg-white/10 backdrop-blur-lg rounded-xl`}>
 <h3 className="text-white text-sm font-semibold mb-3">💬 Conversation</h3>
 <div className={`space-y-3 ${compact ? 'max-h-40' : 'max-h-96'} overflow-y-auto custom-scrollbar`}>
 {conversation.map((msg, index) => (
 <div
 key={index}
 className={`p-3 rounded-lg ${
 msg.type === 'user'
 ? 'bg-blue-500/20 ml-4'
 : 'bg-green-500/20 mr-4'
 }`}
 >
 <div className="flex items-start gap-2">
 <span className="text-lg">
 {msg.type === 'user' ? '👤' : '🤖'}
 </span>
 <div className="flex-1">
   <p className="text-white text-sm font-medium leading-relaxed">{msg.message}</p>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Link to Full Screen */}
 {compact && (
 <div className="mt-4 text-center">
 <a 
 href="/voiceassistant" 
 target="_blank" 
 rel="noopener noreferrer"
 className="text-blue-400 hover:text-blue-300 text-xs font-bold underline flex items-center justify-center gap-1"
 >
 Go to Full Screen Assistant ↗
 </a>
 </div>
 )}

 {/* Setup Instructions */}
 {!compact && (
 <div className="bg-secondary/20 backdrop-blur-lg rounded-xl p-6 mt-6">
 <h3 className="text-primary text-lg font-semibold mb-2">⚙️ Setup Instructions</h3>
 <div className="text-primary text-sm space-y-2">
 <p>1. Create a <code className="bg-bgMain/20 px-2 py-1 rounded">.env</code> file in the project root</p>
 <p>2. Add: <code className="bg-bgMain/20 px-2 py-1 rounded">REACT_APP_OPENAI_API_KEY=your_api_key</code></p>
 <p>3. Get your API key from: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">OpenAI Platform</a></p>
 <p>4. Without the API key, I'll use intelligent fallback responses</p>
 </div>
 </div>
 )}
        </div>
      </div>
    </div>
  );
};

export default NewVoiceAssistant;








